import { Copy, Tag } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { copyText } from "@/lib/api";
import { fmtISO, activePromos, todayISO } from "@/lib/seat";
import { hasPrice, fmtRp } from "@/lib/price";

export const DETAIL_FIELDS = [
  ["highlight", "Highlight Destinasi"], ["airline", "Maskapai"], ["baggage", "Bagasi"], ["cabin_baggage", "Bagasi Kabin"],
  ["flight_schedule", "Jadwal Terbang"], ["requirements", "Syarat"], ["insurance", "Batas Asuransi"], ["dp", "DP"],
  ["bonus", "Bonus"], ["discount", "Diskon"], ["mandatory_shops", "Toko Wajib"], ["optional_tour", "Optional Tour"],
  ["hotel", "Hotel"], ["cnb", "CNB"], ["infant", "Infant"], ["single_supplement", "Single Supplement"], ["remarks", "Remarks"],
];

const copy = async (text, label) => ((await copyText(text)) ? toast.success(`${label} disalin`) : toast.error("Gagal menyalin"));

const TextBlock = ({ title, text, testid }) => (
  <div className="rounded-lg border border-slate-200">
    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
      <span className="text-sm font-medium text-slate-800">{title}</span>
      <button onClick={() => copy(text, title)} className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline" data-testid={testid}><Copy className="h-3.5 w-3.5" /> Salin</button>
    </div>
    <p className="max-h-64 overflow-y-auto whitespace-pre-line px-4 py-3 text-sm text-slate-600">{text}</p>
  </div>
);

const PriceTable = ({ deps }) => (
  <div className="sm:col-span-2" data-testid="seat-detail-price-table">
    <dt className="overline-label">Harga per Tanggal</dt>
    <dd className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full min-w-[560px] text-xs">
        <thead className="bg-slate-50 text-left text-slate-500"><tr><th className="px-3 py-2">Tanggal</th><th className="px-3 py-2">Basic</th><th className="px-3 py-2">Setelah Disc.</th><th className="px-3 py-2">Rincian</th><th className="px-3 py-2 text-right">Total</th></tr></thead>
        <tbody className="divide-y divide-slate-100">
          {deps.map((d) => (
            <tr key={d.id} className="align-top">
              <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-800">{fmtISO(d.date)}{d.operator && <span className="ml-1 text-slate-400">{d.operator}</span>}</td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums">{fmtRp(d.price.basic)}</td>
              <td className="whitespace-nowrap px-3 py-2 tabular-nums">{fmtRp(d.price.discounted)}</td>
              <td className="px-3 py-2 text-slate-600">{d.price.items.filter((i) => i.amount != null).map((i, k) => <div key={k} className="whitespace-nowrap tabular-nums">{i.label || "Lain-lain"}: {fmtRp(i.amount)}</div>)}</td>
              <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-slate-900">{fmtRp(d.price.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </dd>
  </div>
);

const Field = ({ p, k, label }) => (
  <div className={["optional_tour", "remarks", "hotel", "highlight"].includes(k) ? "sm:col-span-2" : ""}>
    <dt className="overline-label">{label}</dt>
    <dd className="mt-1 whitespace-pre-line text-sm text-slate-700">{p[k]}</dd>
  </div>
);

export const ProductDetail = ({ product: p, templates, onClose }) => {
  const priced = p ? p.departures.filter((d) => d.date >= todayISO() && hasPrice(d.price)) : [];
  const cut = DETAIL_FIELDS.findIndex(([k]) => k === "cnb");
  const linked = p ? templates.filter((t) => p.template_ids?.includes(t.id)) : [];
  const active = p ? activePromos(p) : [];
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl" data-testid="seat-detail-dialog">
        {p && <>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{p.name} {p.days && `(${p.days})`}</DialogTitle>
            <DialogDescription>{[p.group, p.departure_city && `Berangkat dari ${p.departure_city}`, p.operator && `Operator ${p.operator}`].filter(Boolean).join(" \u2022 ")}</DialogDescription>
          </DialogHeader>
          {(p.promos || []).length > 0 && (
            <div className="space-y-1.5">
              {p.promos.map((x, i) => (
                <p key={i} className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ${active.includes(x) ? "bg-amber-50 text-amber-900" : "bg-slate-50 text-slate-500"}`}>
                  <Tag className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span><b>{x.note}</b> {(x.start || x.end) && `(${x.start ? fmtISO(x.start) : "..."} s/d ${x.end ? fmtISO(x.end) : "..."})`}{!active.includes(x) && " \u2022 tidak aktif"}</span>
                </p>
              ))}
            </div>
          )}
          <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
            {DETAIL_FIELDS.slice(0, cut).filter(([k]) => p[k]).map(([k, label]) => <Field key={k} p={p} k={k} label={label} />)}
            {priced.length > 0 && <PriceTable deps={priced} />}
            {DETAIL_FIELDS.slice(cut).filter(([k]) => p[k]).map(([k, label]) => <Field key={k} p={p} k={k} label={label} />)}
          </dl>
          {linked.length > 0 && (
            <div className="space-y-3">
              <p className="overline-label">Balasan Cepat Terkait</p>
              {linked.map((t) => <TextBlock key={t.id} title={t.title} text={t.content} testid={`seat-detail-copy-template-${t.id}`} />)}
            </div>
          )}
          {p.itinerary_regular && <TextBlock title="Itinerary Reguler" text={p.itinerary_regular} testid="seat-detail-copy-regular" />}
          {p.itinerary_promo && <TextBlock title="Itinerary Promo" text={p.itinerary_promo} testid="seat-detail-copy-promo" />}
        </>}
      </DialogContent>
    </Dialog>
  );
};
